import type { OpinionContextType } from '@/modules/opinions';
import {
  OpinionPoll,
  OpinionPollState,
  OpinionPollVoted,
} from '@/modules/opinions';

import { ReactNode, createContext, useEffect, useState } from 'react';

export const OpinionSlideContext = createContext<OpinionContextType | null>(
  null,
);

type OpinionSlideProviderProps = {
  opinion: OpinionPoll;
  children: ReactNode;
};

const sliderInitValue = 50;

export const OpinionSlideProvider = (props: OpinionSlideProviderProps) => {
  const [opinion, setOpinion] = useState(props.opinion);
  const [isVoted, setIsVoted] = useState(
    opinion.state === OpinionPollState.VOTED,
  );
  const [answerItemId, setAnswerItemId] = useState(
    isVoted ? opinion.votedFor! : null,
  );
  const [isDragEnded, setIsDragEnded] = useState(isVoted);
  const [sliderValue, setSliderValue] = useState(sliderInitValue);
  const [slidesCount, setSlidesCount] = useState(0);

  useEffect(() => {
    if (
      isVoted &&
      !(opinion as OpinionPollVoted).actualPercentage &&
      opinion.votersChoices
    ) {
      const actualPercentage = opinion.votedFor
        ? (opinion.votersChoices[opinion.votedFor] /
            Object.values(opinion.votersChoices).reduce((a, b) => a + b, 0)) *
          100
        : 0;

      setSliderValue(actualPercentage);
    }
  }, []);

  useEffect(() => {
    const isVoted = opinion.state === OpinionPollState.VOTED;
    setOpinion(opinion);
    setIsVoted(isVoted);
    setIsDragEnded(isVoted);
    setAnswerItemId(isVoted ? opinion.votedFor! : '');

    if (!isVoted) setSliderValue(sliderInitValue);

    if (
      isVoted &&
      (opinion as OpinionPollVoted).actualPercentage !== undefined
    ) {
      setSliderValue(
        Math.min((opinion as OpinionPollVoted).actualPercentage, 100),
      );
    }
  }, [opinion]);

  const contextValue = isVoted
    ? {
        isVoted: true as const,
        opinion: opinion as OpinionPollVoted,
      }
    : {
        isVoted: false as const,
        opinion,
      };

  return (
    <OpinionSlideContext.Provider
      value={{
        ...contextValue,
        answerItemId,
        setAnswerItemId,
        isDragEnded,
        setIsDragEnded,
        sliderValue,
        setSliderValue,
        setOpinion,
        setIsVoted,
        setSlidesCount,
        slidesCount,
      }}
    >
      {props.children}
    </OpinionSlideContext.Provider>
  );
};
